import React from 'react';
import { partitionComponents, isComponent, useTheme } from '@redsift/design-system';
import { TooltipContent } from '../tooltip-content';
import { TooltipTrigger } from '../tooltip-trigger';

import { TooltipContext } from './context';
import { TooltipPlacement, TooltipProps } from './types';
import { useTooltip } from './useTooltip';

import { ThemeProvider } from '../../../../design-system/src/components/theme/context';

const COMPONENT_NAME = 'Tooltip';
const CLASSNAME = 'redsift-tooltip';

/**
 * Tooltip displays brief informational text on hover or focus.
 * Built on Floating UI for smart positioning.
 *
 * Use for non-essential supplementary information. For interactive
 * content, use Popover or Toggletip instead.
 *
 * Placements: `top`, `bottom`, `left`, `right`
 *
 * @example
 * // Basic tooltip
 * <Tooltip>
 *   <Tooltip.Trigger>
 *     <Button>Hover me</Button>
 *   </Tooltip.Trigger>
 *   <Tooltip.Content>Additional information</Tooltip.Content>
 * </Tooltip>
 *
 * @example
 * // Icon with tooltip
 * <Tooltip placement="right">
 *   <Tooltip.Trigger>
 *     <IconButton icon={mdiInformation} aria-label="Info" />
 *   </Tooltip.Trigger>
 *   <Tooltip.Content>This action cannot be undone</Tooltip.Content>
 * </Tooltip>
 *
 * @example
 * // Inline usage with abbreviation
 * <Text>
 *   Configure your <Tooltip>
 *     <Tooltip.Trigger><abbr>SPF</abbr></Tooltip.Trigger>
 *     <Tooltip.Content>Sender Policy Framework</Tooltip.Content>
 *   </Tooltip> record.
 * </Text>
 *
 * @example
 * // Custom delay and offset (for tooltips near clickable elements)
 * <Tooltip delay={500} offset={10}>
 *   <Tooltip.Trigger><Badge>New</Badge></Tooltip.Trigger>
 *   <Tooltip.Content>Released this week</Tooltip.Content>
 * </Tooltip>
 */
export const BaseTooltip: React.FC<TooltipProps> & {
  displayName?: string;
  className?: string;
} = (props) => {
  const {
    children,
    color,
    defaultOpen,
    delay = 300,
    isOpen,
    offset,
    onOpen,
    placement = TooltipPlacement.top,
    theme: propsTheme,
    tooltipId,
    triggerClassName,
  } = props;

  const theme = useTheme ? useTheme(propsTheme) : undefined;

  const tooltip = useTooltip({
    color,
    defaultOpen,
    delay,
    placement,
    isOpen,
    offset,
    onOpen,
    tooltipId,
    theme,
    triggerClassName,
  });

  const [[trigger], [content]] = partitionComponents(React.Children.toArray(children), [
    isComponent('TooltipTrigger'),
    isComponent('TooltipContent'),
  ]);

  return (
    <ThemeProvider value={{ theme }}>
      <TooltipContext.Provider value={tooltip}>
        {trigger && isComponent('TooltipTrigger')(trigger) ? trigger : null}
        {content && isComponent('TooltipContent')(content) ? content : null}
      </TooltipContext.Provider>
    </ThemeProvider>
  );
};
BaseTooltip.className = CLASSNAME;
BaseTooltip.displayName = COMPONENT_NAME;

export const Tooltip = Object.assign(BaseTooltip, {
  Trigger: TooltipTrigger,
  Content: TooltipContent,
});
